import SEO from '../../components/base/SEO';

export default function CatalogPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Product Catalog - Proform Chimney Systems',
    description: 'Browse our complete range of chimney systems. Download comprehensive product catalog with specifications and installation guides.',
    url: `${siteUrl}/catalog`,
    publisher: {
      '@type': 'Organization',
      name: 'Proform Chimney Systems'
    }
  };

  const categories = [
    {
      icon: 'ri-home-4-fill',
      title: 'Residential',
      description: 'Home heating solutions',
      image: 'https://public.readdy.ai/ai/img_res/ccb76c70139b3bc5c4c13f5f795cef49.jpg'
    },
    {
      icon: 'ri-restaurant-fill',
      title: 'Commercial',
      description: 'Restaurant and kitchen systems',
      image: 'https://readdy.ai/api/search-image?query=Professional%20commercial%20kitchen%20with%20industrial%20ventilation%20system%2C%20stainless%20steel%20equipment%2C%20modern%20restaurant%20kitchen%20design%2C%20clean%20organized%20workspace%2C%20professional%20culinary%20environment&width=800&height=600&seq=cat-commercial&orientation=landscape'
    },
    {
      icon: 'ri-building-fill',
      title: 'Industrial',
      description: 'Factory chimney systems',
      image: 'https://readdy.ai/api/search-image?query=Large%20industrial%20factory%20building%20with%20tall%20chimney%20stack%2C%20modern%20manufacturing%20facility%20exterior%2C%20industrial%20architecture%2C%20professional%20industrial%20photography%20with%20clear%20sky&width=800&height=600&seq=cat-industrial&orientation=landscape'
    },
    {
      icon: 'ri-leaf-fill',
      title: 'Eco-Friendly',
      description: 'Pellet and green solutions',
      image: 'https://readdy.ai/api/search-image?query=Eco-friendly%20modern%20home%20with%20sustainable%20heating%20system%2C%20green%20energy%20solution%2C%20contemporary%20ecological%20design%2C%20natural%20materials%2C%20environmentally%20conscious%20architecture&width=800&height=600&seq=cat-eco&orientation=landscape'
    }
  ];

  return (
    <>
      <SEO
        title="Chimney Systems Catalog - Download Product Guide | Proform Turkey"
        description="Download Proform Chimney Systems complete product catalog. Detailed specifications, technical drawings, and installation guides for residential, commercial, and industrial chimney systems."
        keywords="chimney catalog, product guide Turkey, chimney specifications, chimney technical drawings, chimney installation guide"
        canonical={`${siteUrl}/catalog`}
        schema={schema}
      />
      <div className="min-h-screen bg-white pt-20">
        {/* Hero Section */}
        <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20product%20catalog%20layout%20on%20modern%20desk%2C%20organized%20technical%20documents%20and%20specifications%2C%20clean%20workspace%20with%20architectural%20drawings%2C%20professional%20business%20photography%20with%20natural%20lighting&width=1920&height=1080&seq=catalog-hero-new&orientation=landscape"
              alt="Catalog Hero"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-6">
            <div className="inline-block bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 px-6 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-book-open-fill mr-2"></i>
              Product Catalog
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6">Catalog</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto font-light">
              Explore all our chimney systems
            </p>
          </div>
        </section>

        {/* Catalog Content */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Download Our Catalog
              </h2>
              <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
              <p className="text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
                Explore our comprehensive product catalog featuring detailed technical specifications, technical drawings, and installation guides for all our chimney systems.
              </p>
            </div>

            {/* Main Catalog Card */}
            <div className="max-w-4xl mx-auto mb-24">
              <div className="bg-gradient-to-br from-orange-50 via-white to-orange-50 rounded-3xl shadow-2xl overflow-hidden border-2 border-orange-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                  {/* Left side - Visual */}
                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-12 flex flex-col items-center justify-center text-white">
                    <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-3xl flex items-center justify-center mb-8 shadow-xl">
                      <i className="ri-file-pdf-fill text-8xl text-white"></i>
                    </div>
                    <h3 className="text-3xl font-bold mb-3">General Catalog</h3>
                    <p className="text-white/90 text-center text-lg">Comprehensive overview of all products</p>
                    <div className="mt-8 flex items-center space-x-6 text-sm">
                      <div className="flex items-center">
                        <i className="ri-file-text-fill text-2xl mr-2"></i>
                        <span>PDF Format</span>
                      </div>
                      <div className="flex items-center">
                        <i className="ri-download-cloud-fill text-2xl mr-2"></i>
                        <span>Free</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side - Content */}
                  <div className="p-12 flex flex-col justify-center">
                    <h4 className="text-2xl font-bold text-gray-900 mb-6">Catalog Contents</h4>
                    <ul className="space-y-4 mb-8">
                      <li className="flex items-start">
                        <i className="ri-check-line text-orange-500 text-2xl mr-3 flex-shrink-0"></i>
                        <span className="text-gray-700">Detailed descriptions of all product series</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-orange-500 text-2xl mr-3 flex-shrink-0"></i>
                        <span className="text-gray-700">Technical specifications and dimensions</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-orange-500 text-2xl mr-3 flex-shrink-0"></i>
                        <span className="text-gray-700">Installation guides and diagrams</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-orange-500 text-2xl mr-3 flex-shrink-0"></i>
                        <span className="text-gray-700">Product images and technical drawings</span>
                      </li>
                    </ul>
                    <a
                      href="https://drive.google.com/file/d/1v2PZL2A1kedHfq8XUtj9Utfjkspg7PqJ/view?usp=sharing"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center bg-orange-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-download-line text-2xl mr-3"></i>
                      Download Catalog
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Categories */}
            <div className="mb-16">
              <div className="text-center mb-12">
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Product Categories</h3>
                <p className="text-gray-700">Solutions for every need</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {categories.map((category, index) => (
                  <div
                    key={index}
                    className="group relative bg-white rounded-3xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    {/* Image Background */}
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={category.image}
                        alt={category.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                      
                      {/* Content Overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <div className="flex items-center space-x-4">
                          <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0">
                            <i className={`${category.icon} text-white text-3xl`}></i>
                          </div>
                          <div>
                            <h4 className="text-2xl font-bold text-white mb-1">{category.title}</h4>
                            <p className="text-white/90">{category.description}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-4">Why Proform Catalog?</h3>
              <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-10 rounded-3xl shadow-lg text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-file-list-3-fill text-white text-4xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Detailed Information</h4>
                <p className="text-gray-700 leading-relaxed">
                  Comprehensive technical specifications and detailed descriptions for every product
                </p>
              </div>

              <div className="bg-white p-10 rounded-3xl shadow-lg text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-image-fill text-white text-4xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Visual Richness</h4>
                <p className="text-gray-700 leading-relaxed">
                  High-quality product images and technical drawings
                </p>
              </div>

              <div className="bg-white p-10 rounded-3xl shadow-lg text-center hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className="ri-tools-fill text-white text-4xl"></i>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-3">Installation Guide</h4>
                <p className="text-gray-700 leading-relaxed">
                  Step-by-step installation instructions and setup diagrams
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-orange-500 via-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Need More Information?
            </h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Contact our team for custom solutions and detailed product information
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="/contact"
                className="inline-block bg-white text-orange-600 px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-2"></i>
                Contact Us
              </a>
              <a
                href="/services"
                className="inline-block bg-gray-900 text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-gray-800 transition-all duration-300 transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-service-fill mr-2"></i>
                Our Services
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}